// Something a guest started that survives into their real account.
//
// Linking (see `lib/auth.ts`) keeps the anonymous UID and makes this unnecessary
// for brand-new accounts, which is the common case. It's still needed when the
// user signs into an account that already exists: Firebase refuses the link,
// the UID changes, and whatever the guest did is stranded under the old one.
//
// localStorage, not sessionStorage: the gap between scanning a QR at a festival
// and actually signing up is often a day, not a tab session.

export type PendingIntentKind = 'groupJoin' | 'totemFollow' | 'invite'

const KEY_PREFIX = 'beacon_pending_'

const key = (kind: PendingIntentKind) => `${KEY_PREFIX}${kind}`

export function setPendingIntent(kind: PendingIntentKind, value: string): void {
  try {
    localStorage.setItem(key(kind), value)
  } catch {
    // Private browsing / storage full — the intent is a convenience, not a
    // correctness requirement, so losing it must not break the flow.
  }
}

export function peekPendingIntent(kind: PendingIntentKind): string | null {
  try {
    return localStorage.getItem(key(kind))
  } catch {
    return null
  }
}

/** Reads and clears in one step — a pending intent is applied at most once. */
export function takePendingIntent(kind: PendingIntentKind): string | null {
  const value = peekPendingIntent(kind)
  clearPendingIntent(kind)
  return value
}

export function clearPendingIntent(kind: PendingIntentKind): void {
  try {
    localStorage.removeItem(key(kind))
  } catch {
    // Ignore — see setPendingIntent.
  }
}
